import { useState, useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// "Nice" distance values in metres
const NICE_VALUES = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500,
  1000, 2000, 5000, 10000];

function computeScaleBar(viewer) {
  const canvas = viewer.canvas;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  const ellipsoid = viewer.scene.globe.ellipsoid;

  const p1 = viewer.camera.pickEllipsoid(
    new Cesium.Cartesian2(w / 2, h / 2),
    ellipsoid,
  );
  const p2 = viewer.camera.pickEllipsoid(
    new Cesium.Cartesian2(w / 2 + 100, h / 2),
    ellipsoid,
  );

  if (!p1 || !p2) return null;

  const metersPerPixel = Cesium.Cartesian3.distance(p1, p2) / 100;
  if (metersPerPixel <= 0) return null;

  const targetMeters = metersPerPixel * 150;

  // Largest nice value ≤ targetMeters
  let niceValue = NICE_VALUES[0];
  for (const v of NICE_VALUES) {
    if (v <= targetMeters) niceValue = v;
    else break;
  }

  const barPixelWidth = niceValue / metersPerPixel;
  const label = niceValue >= 1000
    ? `${niceValue / 1000} km`
    : `${niceValue} m`;

  return { width: barPixelWidth, label };
}

/**
 * Renders a dynamic scale bar overlay.
 * Updates every frame via postRender.
 *
 * @param {{ viewer: Cesium.Viewer, sx?: object, color?: string }} props
 */
export function ScaleBar({ viewer, sx, color = 'rgba(255,255,255,0.85)' }) {
  const [bar, setBar] = useState(null);
  const frameRef = useRef(null);

  useEffect(() => {
    if (!viewer) return;

    const onPostRender = () => {
      // Throttle: skip if a frame is already pending
      if (frameRef.current) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        if (!viewer || viewer.isDestroyed()) return;
        try {
          setBar(computeScaleBar(viewer));
        } catch {
          setBar(null);
        }
      });
    };

    viewer.scene.postRender.addEventListener(onPostRender);
    return () => {
      if (!viewer.isDestroyed()) {
        viewer.scene.postRender.removeEventListener(onPostRender);
      }
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [viewer]);

  if (!bar) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 40,
        left: 12,
        zIndex: 1100,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...sx,
      }}
    >
      {/* Ruler bar: left cap | bottom border | right cap */}
      <Box
        sx={{
          width: bar.width,
          height: 8,
          borderLeft: `2px solid ${color}`,
          borderRight: `2px solid ${color}`,
          borderBottom: `2px solid ${color}`,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color,
          fontSize: '0.68rem',
          lineHeight: 1.2,
          mt: '2px',
          textShadow: color.startsWith('rgba(0') || color.startsWith('rgb(0') || color === 'black'
            ? '0 1px 3px rgba(255,255,255,0.7)'
            : '0 1px 3px rgba(0,0,0,0.7)',
          userSelect: 'none',
        }}
      >
        {bar.label}
      </Typography>
    </Box>
  );
}

export default ScaleBar;
